import type { ChartComponentSpec, AdaptedData } from "./types";
import { STYLE_PRESETS, AXES_PRESETS, Y_AXIS_INDEX } from "./presets";
import { formatSeriesLabel } from "./seriesLabel";
import { resolveSeriesMisc } from "./seriesMisc";

/**
 * Standard stacked-bar color palette for zone coloring.
 * Indexed purely by position within a stack (bottom = 0).
 * The same position always produces the same color regardless of stack size.
 */
const STACKED_BAR_COLORS = [
  "#90EE90", // light green
  "#00AB41", // dark green
  "#FFD300", // yellow
  "#FFA500", // orange
  "#DB0F27", // dark red
];

/** Highcharts series type for a line series, keyed by `${isCurved}-${isArea}`. */
const LINE_SERIES_TYPES: Record<string, string> = {
  "false-false": "line",
  "true-false": "spline",
  "false-true": "area",
  "true-true": "areaspline",
};

// Lighter than the Highcharts default of 0.75.
const AREA_FILL_OPACITY = 0.4;

function stackedBarColor(positionInStack: number): string {
  if (positionInStack < STACKED_BAR_COLORS.length) return STACKED_BAR_COLORS[positionInStack];
  return `hsl(${positionInStack * 30}, 70%, 50%)`;
}

/**
 * Produce a vanilla Highcharts config from the ComponentSpec + adapted data.
 * Handles series types, axis presets, stacking. No mutations applied.
 *
 * ACWR series are treated uniformly — y-axis and chart type come from the spec
 * like every other series. Curved lines and bar labels are controlled by
 * per-series plotMutation flags rather than metric-type checks.
 */
export function buildRawConfig(
  spec: ChartComponentSpec,
  adaptedData: AdaptedData,
): Record<string, unknown> {
  const { categories, labels, seriesDataMap } = adaptedData;
  const style = STYLE_PRESETS.default;

  // Determine axis layout — single or dual based on whether any series uses the right axis.
  const hasRight = spec.series.some((s) => s.yAxis === "right");
  const axisKey = hasRight ? "dual" : "single";
  const axes = AXES_PRESETS[axisKey];

  const hasStacking = spec.series.some((s) => s.stackOn != null);

  // When ACWR is toggled on, hide the parent metric series — only the ACWR
  // companion should render. Collect queryIds that serve as parents of ACWR series.
  const acwrParentIds = new Set(
    spec.series
      .filter((s) => s.series_builder_misc?.isAcwrCompanion)
      .map((s) => s.series_builder_misc.parentQueryId)
      .filter(Boolean),
  );
  const visibleSeries = spec.series.filter((s) => !acwrParentIds.has(s.queryId));

  // Build stack groups so each series knows its position within its own stack.
  // Key = resolved Highcharts stack id (stackOn ?? queryId), Value = ordered queryIds.
  const stackGroups = new Map<string, string[]>();
  for (const sd of visibleSeries) {
    if (sd.chartType !== "bar") continue;
    const resolvedStack = sd.stackOn ?? sd.queryId;
    const group = stackGroups.get(resolvedStack) ?? [];
    group.push(sd.queryId);
    stackGroups.set(resolvedStack, group);
  }

  // Same markup as the Highcharts default point line, with the backend value_label
  // in place of the raw y so the tooltip shows the metric's decimal count too.
  const tooltipPointFormatter = function (this: Highcharts.Point) {
    const custom = (this.options as { custom?: { value_label?: string } }).custom;
    const label = custom?.value_label ?? (this.y != null ? String(this.y) : "");
    return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${label}</b><br/>`;
  };

  const hcSeries = visibleSeries.map((sd, i) => {
    const adapted = seriesDataMap.get(sd.queryId);
    const name = sd.displayName || adapted?.name || `Series ${i + 1}`;
    // The legend names the subjects the data query reads: an ACWR companion's parent,
    // then Plot 1 unless the plot picked its own players.
    const labelMisc = resolveSeriesMisc(sd, spec.series).subject;
    // That resolution may hand back ANOTHER series' misc, to borrow its subject names.
    // The count spec must not come along: it belongs to this series' own metric, and
    // inheriting it would label plot 2 with plot 1's include-list.
    const labelMiscForSeries = labelMisc
      ? { ...labelMisc, countSpec: sd.series_builder_misc?.countSpec }
      : labelMisc;
    const seriesLabel = sd.displayNameOverride?.trim()
      ? sd.displayNameOverride
      : formatSeriesLabel(name, sd.metricAgg, sd.metricIdentifierType, labelMiscForSeries);

    const isLine = sd.chartType !== "bar";
    const isCurved = isLine && sd.plotMutations.lineSmoothing === true;
    const isArea = isLine && sd.plotMutations.areaFill === true;
    const lineType = LINE_SERIES_TYPES[`${isCurved}-${isArea}`];
    const rawPoints = adapted?.points ?? categories.map(() => null);
    const hasNoData = rawPoints.every((p) => p === null || p === undefined);
    // Strip per-point colors on line/spline series so markers inherit the series color.
    // Keep `custom`: it carries the backend value_label the data label and tooltip show.
    const stripColor = (p: (typeof rawPoints)[number]) =>
      p != null && typeof p !== "number" ? (p.custom ? { y: p.y, custom: p.custom } : p.y) : p;
    const data = isLine ? rawPoints.map(stripColor) : rawPoints;

    const showDataLabels = sd.plotMutations.showDataLabels === true;

    // Per-series zone coloring: only the checked series changes color.
    // Position is derived from the series' own stack group independently.
    const hasColorOverride = sd.plotMutations.colorOverride === true && !!sd.colorOverride;
    let seriesColor = hasColorOverride ? sd.colorOverride! : style.colors[i % style.colors.length];
    let useOverrideColor = hasColorOverride;
    if (!isLine && sd.plotMutations.OverrideColoringStrategyForStackedBar) {
      const resolvedStack = sd.stackOn ?? sd.queryId;
      const members = stackGroups.get(resolvedStack);
      if (members && members.length >= 2) {
        const positionInStack = members.indexOf(sd.queryId);
        seriesColor = stackedBarColor(positionInStack);
        useOverrideColor = true;
      }
    }

    // When override coloring is active, strip per-point colors so the series color wins.
    const finalData = useOverrideColor ? rawPoints.map(stripColor) : data;

    // The backend formats value_label to the metric's decimal count. Without one,
    // show the value as it is: a fixed count here would override that setting.
    const dataLabelFormatter = function (this: Highcharts.PointLabelObject) {
      const custom = (this.point.options as { custom?: { value_label?: string } }).custom;
      return custom?.value_label ?? (this.y != null ? String(this.y) : null);
    };

    return {
      name: seriesLabel,
      type: isLine ? lineType : "column",
      data: finalData,
      yAxis: Y_AXIS_INDEX[sd.yAxis] ?? 0,
      color: seriesColor,
      // Areas sit behind bars, plain lines in front of them.
      zIndex: isArea ? -9999 : isLine ? 1 : 0,
      ...(isArea && { fillOpacity: AREA_FILL_OPACITY }),
      // _queryId is used by applyMutations to locate this series by identity rather than fragile index.
      _queryId: sd.queryId,
      _hasNoData: hasNoData,
      ...(!isLine && {
        stack: sd.stackOn ?? sd.queryId,
        ...(showDataLabels && {
          dataLabels: {
            enabled: true,
            inside: true,
            align: "center",
            verticalAlign: "middle",
            formatter: dataLabelFormatter,
            style: { color: "#000000", textOutline: "1px rgba(255,255,255,0.6)", fontSize: "11px", fontWeight: "600" },
          },
        }),
      }),
      ...(isLine && showDataLabels && {
        dataLabels: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          y: -6,
          formatter: dataLabelFormatter,
          style: { color: "#000000", textOutline: "1px contrast", fontSize: "10px", fontWeight: "600" },
        },
      }),
    };
  });

  return {
    chart: { type: "column", backgroundColor: "transparent", inverted: !!spec.display.inverted },
    title: { text: "" },
    xAxis: { categories: labels, gridLineWidth: 1, tickWidth: 1, tickLength: 6, tickColor: "#000", lineColor: "#000" },
    yAxis: hasStacking
      ? axes.yAxis.map((ax, i) => (i === 0 ? { ...ax, reversedStacks: false } : ax))
      : axes.yAxis,
    tooltip: { shared: true, pointFormatter: tooltipPointFormatter },
    legend: {
      ...style.legend,
      enabled: spec.display.showLegend,
      labelFormatter: function (this: any) {
        return this.userOptions?._hasNoData
          ? `${this.name} (No data in range)`
          : this.name;
      },
    },
    plotOptions: {
      ...style.plotOptions,
      ...(hasStacking && {
        column: {
          ...(style.plotOptions?.column ?? {}),
          stacking: "normal" as const,
          borderColor: "rgba(255,255,255,0.8)",
          borderWidth: 1,
        },
      }),
    },
    series: hcSeries,
    credits: { enabled: false },
  };
}
